import { z } from 'zod';
import { SourceRef } from './common.js';

const Palette = z.object({
  base: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
  belly: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
  markings: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
  mask: z.string().min(1),
});

export const Morph = z.object({
  id: z.string().regex(/^[a-z][a-z0-9_]*$/),
  name: z.string().min(1),
  tier: z.enum(['common', 'uncommon', 'rare', 'very_rare']),
  wild_odds_one_in: z.number().int().positive().nullable(),
  price_multiplier: z.number().positive(),
  palette: Palette,
  /** Polygenic dosage params (Sim-Core genotype.ts; defaults 2 loci / threshold 3).
   *  Optional so existing polygenic morphs validate unchanged. */
  polygenic: z.object({ loci: z.number().int().min(1).max(6).optional(), threshold: z.number().int().min(1).optional() }).optional(),
  genetics: z.object({
    mode: z.enum(['wild_type', 'recessive', 'dominant', 'co_dominant', 'polygenic', 'sex_linked']),
    note: z.string(),
  }),
  /** Optional explicit inheritance (Sim-Core genotype.ts reads it; wins over genetics.mode).
   *  basis says how well the dominance call is grounded: 'sourced' needs source + quote. */
  inheritance: z.object({
    dominance: z.enum(['recessive', 'dominant', 'co_dominant']),
    heterozygote_morph: z.string().optional(),
    basis: z.enum(['sourced', 'analogy', 'design_default']),
    source: z.string().optional(),
    quote: z.string().optional(),
    note: z.string().optional(),
  }).optional(),
  anatomical_basis: z.string().min(1),
  sources: z.array(z.string()).min(1),
}).superRefine((m, ctx) => {
  if (m.tier === 'common' && m.wild_odds_one_in !== null) {
    ctx.addIssue({ code: 'custom', message: 'common tier must have null wild odds', path: ['wild_odds_one_in'] });
  }
  if (m.tier !== 'common' && m.wild_odds_one_in === null) {
    ctx.addIssue({ code: 'custom', message: 'non-common tier needs wild odds', path: ['wild_odds_one_in'] });
  }
});

export const SpeciesMorphs = z.object({
  species: z.string().regex(/^[a-z][a-z0-9_]*$/),
  morphs: z.array(Morph).min(1),
  references: z.array(SourceRef).min(1),
}).superRefine((f, ctx) => {
  const refIds = new Set(f.references.map((r) => r.id));
  const commons = f.morphs.filter((m) => m.tier === 'common');
  if (commons.length !== 1) ctx.addIssue({ code: 'custom', message: 'exactly one common (wild type) morph required', path: ['morphs'] });
  const morphIds = new Set(f.morphs.map((m) => m.id));
  for (const m of f.morphs) {
    const inh = m.inheritance;
    if (inh) {
      if (inh.basis === 'sourced' && (!inh.source || !inh.quote || !refIds.has(inh.source))) {
        ctx.addIssue({ code: 'custom', message: `morph ${m.id}: sourced inheritance needs a known source and a quote`, path: ['morphs'] });
      }
      if (inh.dominance === 'co_dominant' && (!inh.heterozygote_morph || !morphIds.has(inh.heterozygote_morph))) {
        ctx.addIssue({ code: 'custom', message: `morph ${m.id}: co_dominant needs heterozygote_morph naming a morph of this species`, path: ['morphs'] });
      }
      if (inh.dominance !== 'co_dominant' && inh.heterozygote_morph) {
        ctx.addIssue({ code: 'custom', message: `morph ${m.id}: heterozygote_morph only valid for co_dominant`, path: ['morphs'] });
      }
    }
    for (const id of m.sources) if (!refIds.has(id)) {
      ctx.addIssue({ code: 'custom', message: `morph ${m.id} cites unknown source '${id}'`, path: ['morphs'] });
    }
  }
});
export type SpeciesMorphs = z.infer<typeof SpeciesMorphs>;
